import { cn } from "@/lib/utils"
import type { SeasonOverviewUser } from "@/lib/api"

/** 14-day workout/rest/empty heatmap across all users with season data.
 *  Spans 7 days past through 6 days ahead, anchored on today. Clicking a
 *  user name fires `onUserClick` (the Users tab routes to the user detail). */
export function SeasonCoverageHeatmap({
  users,
  onUserClick,
}: {
  users: SeasonOverviewUser[]
  onUserClick: (userId: string) => void
}) {
  const today = new Date()
  const dates: string[] = []
  for (let i = -7; i <= 6; i++) {
    const d = new Date(today)
    d.setDate(d.getDate() + i)
    dates.push(d.toISOString().split("T")[0])
  }

  const todayStr = today.toISOString().split("T")[0]

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="font-semibold mb-3">Season Coverage (14 days)</h3>

      <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="size-2.5 rounded-sm bg-green-500" /> Workout
        </span>
        <span className="flex items-center gap-1">
          <span className="size-2.5 rounded-sm bg-blue-200" /> Rest
        </span>
        <span className="flex items-center gap-1">
          <span className="size-2.5 rounded-sm bg-muted" /> Empty
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left font-medium text-muted-foreground pb-2 pr-3 min-w-[120px]">
                User
              </th>
              {dates.map((d) => {
                const day = new Date(d + "T12:00:00")
                const dayName = day
                  .toLocaleDateString("en-US", { weekday: "narrow" })
                return (
                  <th
                    key={d}
                    className={cn(
                      "text-center font-normal text-muted-foreground pb-2 w-7",
                      d === todayStr && "font-bold text-foreground",
                    )}
                  >
                    {dayName}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="group">
                <td className="pr-3 py-0.5">
                  <button
                    onClick={() => onUserClick(user.id)}
                    className="text-left hover:text-primary truncate max-w-[120px] block"
                  >
                    {user.first_name} {user.last_name?.charAt(0)}.
                  </button>
                </td>
                {dates.map((d) => {
                  const status = user.dates?.[d]
                  return (
                    <td key={d} className="text-center py-0.5">
                      <span
                        className={cn(
                          "inline-block size-2.5 rounded-sm",
                          status === "workout" && "bg-green-500",
                          status === "rest" && "bg-blue-200",
                          !status && "bg-muted",
                          d === todayStr && "ring-1 ring-foreground/20",
                        )}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <p className="text-center text-muted-foreground text-sm py-6">
          No users with active seasons
        </p>
      )}
    </div>
  )
}
